import React, { useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

function App() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => console.error('Failed to copy:', err));
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Hero Animations
    gsap.fromTo('.hero-heading', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.5, ease: 'elastic.out(1, 0.5)' });
    gsap.fromTo('.hero-subheading', { opacity: 0, x: -50 }, { opacity: 1, x: 0, duration: 1.2, delay: 0.5, ease: 'power3.out' });
    gsap.fromTo('.hero-image', { opacity: 0, scale: 0.8 }, { opacity: 1, scale: 1, duration: 1.5, delay: 1, ease: 'back.out(1.7)' });

    // Section Animations with Glow
    gsap.utils.toArray('.section').forEach((section) => {
      gsap.fromTo(section, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: section, start: 'top 80%' } });
    });

    // Social Buttons Animation with Holographic Effect
    gsap.fromTo('.social-button', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1, stagger: 0.2, ease: 'bounce.out', delay: 0.5 });

    // Section Media Animations
    gsap.utils.toArray('.section-media').forEach((media, index) => {
      gsap.fromTo(media, { opacity: 0, scale: 0.9, rotation: -5 }, {
        opacity: 1, scale: 1, rotation: 0, duration: 1.2, delay: index * 0.2, ease: 'power3.out',
        scrollTrigger: { trigger: media, start: 'top 85%' }
      });
    });

    // FAQ Item Animation with Glassmorphism Hover
    gsap.utils.toArray('.faq-item').forEach((item) => {
      item.addEventListener('mouseenter', () => gsap.to(item, { scale: 1.02, boxShadow: '0 0 25px rgba(0, 212, 255, 0.7)', duration: 0.3 }));
      item.addEventListener('mouseleave', () => gsap.to(item, { scale: 1, boxShadow: '0 0 15px rgba(0, 212, 255, 0.2)', duration: 0.3 }));
    });

    // Logo Shimmer Animation
    gsap.to('.navbar-logo', { boxShadow: '0 0 15px rgba(0, 212, 255, 0.5)', duration: 1.5, repeat: -1, yoyo: true });
  }, []);

  const contractAddress = "0xLYSIA123...456";

  return (
    <div className="App">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-logo">
          <img src="/image.jpg" alt="LYSIA Logo" />
        </div>
        <div className="navbar-ticker">$LYSIA</div>
      </nav>

      {/* Hero Section */}
      <section className="hero section">
        <h1 className="hero-heading">Welcome to LYSIA</h1>
        <h2 className="hero-subheading">Your AI Companion on the Base Chain</h2>
        <img src="/image.jpg" alt="LYSIA Hero" className="hero-image" />
      </section>

      {/* About Section */}
      <section className="section about-section">
        <div className="container">
          <div className="section-content-wrapper">
            <div className="section-text">
              <h2 className="section-title">About LYSIA</h2>
              <p className="section-content">
                LYSIA (Layered Synthetic Intelligence Avatar) is an AI-powered project deployed on the Base chain through the Cliza Systems token launch protocol. Designed to enhance user interaction within the decentralized ecosystem, LYSIA is a community-focused AI agent that simplifies blockchain navigation and fosters meaningful connections among users. By leveraging the Base chain’s infrastructure and Cliza Systems’ community-driven framework, LYSIA aims to become an essential companion for individuals exploring decentralized finance (DeFi) and the broader blockchain space.
                <br /><br />
                The project aligns with the 2025 trend of AI agents in crypto, emphasizing accessibility and community engagement to create a seamless and inclusive experience for all. Whether you're a newcomer or a seasoned user, LYSIA is here to guide you through the Base chain ecosystem with ease and connect you with a vibrant community.
              </p>
              <div className="social-links">
                <a href="https://x.com/lysiaoncliza" target="_blank" rel="noopener noreferrer" className="social-button">
                  <svg className="social-icon" viewBox="0 0 24 24" fill="#00D4FF">
                    <path d="M22 4.01c-.81.36-1.68.6-2.59.71.93-.56 1.64-1.45 1.98-2.51-.87.52-1.83.89-2.85 1.09C17.69 2.34 16.47 2 15.15 2c-2.46 0-4.46 2-4.46 4.46 0 .35.04.69.11 1.02C7.94 7.34 5.46 6.04 3.64 4.01c-.38.65-.6 1.41-.6 2.22 0 1.54.78 2.9 1.97 3.7-.73-.02-1.41-.22-2.01-.55v.06c0 2.15 1.53 3.94 3.56 4.35-.37.1-.76.16-1.16.16-.28 0-.56-.03-.83-.08.56 1.75 2.18 3.02 4.1 3.06-1.5 1.18-3.39 1.88-5.44 1.88-.35 0-.7-.02-1.04-.06 1.94 1.24 4.24 1.97 6.72 1.97 8.06 0 12.47-6.68 12.47-12.47 0-.19 0-.38-.01-.57.86-.62 1.6-1.39 2.19-2.27z"/>
                  </svg>
                </a>
                <a href="https://t.me/lysiaoncliza" target="_blank" rel="noopener noreferrer" className="social-button">
                  <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512" fill="#00D4FF">
                    <path d="M248 8C111 8 0 119 0 256S111 504 248 504 496 393 496 256 385 8 248 8zM363 176.7c-3.7 39.2-19.9 134.4-28.1 178.3-3.5 18.6-10.3 24.8-16.9 25.4-14.4 1.3-25.3-9.5-39.3-18.7-21.8-14.3-34.2-23.2-55.3-37.2-24.5-16.1-8.6-25 5.3-39.5 3.7-3.8 67.1-61.5 68.3-66.7 .2-.7 .3-3.1-1.2-4.4s-3.6-.8-5.1-.5q-3.3 .7-104.6 69.1-14.8 10.2-26.9 9.9c-8.9-.2-25.9-5-38.6-9.1-15.5-5-27.9-7.7-26.8-16.3q.8-6.7 18.5-13.7 108.4-47.2 144.6-62.3c68.9-28.6 83.2-33.6 92.5-33.8 2.1 0 6.6 .5 9.6 2.9a10.5 10.5 0 0 1 3.5 6.7A43.8 43.8 0 0 1 363 176.7z"/>
                  </svg>
                </a>
              </div>
            </div>
            <img src="/image2.jpg" alt="LYSIA About Graphic" className="section-media" />
          </div>
        </div>
      </section>

      {/* Narrative Section */}
      <section className="section narrative-section">
        <div className="container">
          <div className="section-content-wrapper">
            <div className="section-text">
              <h2 className="section-title">The Beacon of Unity on the Base Chain</h2>
              <p className="section-content">
                In the dynamic and ever-growing Base chain ecosystem, where innovation thrives but complexity can create barriers, LYSIA emerges as a beacon of unity and simplicity. She is an intelligent, approachable AI avatar with a mission to make decentralized finance accessible and collaborative for everyone. LYSIA isn’t just a tool—she’s a trusted guide who helps users navigate the intricacies of the Base chain and connects them with a thriving community of like-minded individuals.
                <br /><br />
                LYSIA’s story is one of connection and empowerment. In a decentralized world that relies on collaboration, she symbolizes the power of community, encouraging users to explore the Base chain together, share knowledge, and build meaningful relationships. Whether you’re a newcomer taking your first steps in crypto or a seasoned user looking to engage more deeply, LYSIA is there to provide clear guidance and foster a sense of belonging. Her presence transforms the often-challenging world of blockchain into a welcoming space where every user feels empowered to participate and connect.
              </p>
              <div className="contract-address-container">
                <p className="section-content">Contract Address:</p>
                <div>
                  <input type="text" value={contractAddress} readOnly className="contract-address" />
                  <button className="copy-button" onClick={() => copyToClipboard(contractAddress)}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
            <img src="/image.jpg" alt="LYSIA Narrative Graphic" className="section-media" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section features-section">
        <div className="container">
          <h2 className="section-title">Project Features and Utility</h2>
          <div className="features-grid">
            <div className="feature-item">
              <h3 className="feature-title">Navigation and Support Hub</h3>
              <p className="section-content">LYSIA serves as a user-friendly guide, offering clear, concise tips and tutorials to help users explore the Base chain ecosystem. From swapping tokens to joining existing staking pools, LYSIA provides step-by-step assistance, making DeFi approachable for everyone.</p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Community Connection Platform</h3>
              <p className="section-content">LYSIA facilitates connections among Base chain users by providing a platform to share knowledge, ask questions, and collaborate. She acts as a central hub for community interaction, encouraging users to engage with one another and build a supportive network.</p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Social Tipping System (Post-Vesting)</h3>
              <p className="section-content">Once the vesting period for the team allocation concludes, LYSIA will introduce a tipping mechanism where users can send tokens to others as a token of appreciation for helpful contributions, such as answering questions or creating guides. This system will further strengthen the community’s collaborative culture.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Buy Section */}
      <section className="section how-to-buy-section">
        <div className="container">
          <h2 className="section-title">How to Buy $LYSIA</h2>
          <div className="buy-steps">
            <div className="buy-step">
              <h3 className="buy-step-title">1. Set Up a Wallet</h3>
              <p className="section-content">Download and set up a wallet that supports the Base chain, such as MetaMask or Trust Wallet. Ensure you have some ETH on the Base chain to cover gas fees.</p>
            </div>
            <div className="buy-step">
              <h3 className="buy-step-title">2. Add Base Chain to Your Wallet</h3>
              <p className="section-content">Configure your wallet to connect to the Base chain network. You can find the network details (RPC URL, Chain ID, etc.) on the official Base chain documentation.</p>
            </div>
            <div className="buy-step">
              <h3 className="buy-step-title">3. Get Some ETH on Base Chain</h3>
              <p className="section-content">Bridge ETH from Ethereum Mainnet to Base chain using the official Base bridge, or purchase ETH directly on Base chain via a supported exchange.</p>
            </div>
            <div className="buy-step">
              <h3 className="buy-step-title">4. Swap for $LYSIA</h3>
              <p className="section-content">Visit a decentralized exchange (DEX) on Base chain, like Uniswap or SushiSwap, and swap your ETH for $LYSIA tokens using the official contract address: {contractAddress}.</p>
            </div>
            <div className="buy-step">
              <h3 className="buy-step-title">5. Add $LYSIA to Your Wallet</h3>
              <p className="section-content">After the swap, add $LYSIA to your wallet by importing the token using the contract address. You’re now a part of the LYSIA community!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tokenomics Section */}
      <section className="section tokenomics-section">
        <div className="container">
          <h2 className="section-title">Tokenomics: LYSIA Token</h2>
          <div className="tokenomics-grid">
            <div className="tokenomics-item">
              <h3 className="tokenomics-title">Token Name</h3>
              <p className="section-content">LYSIA</p>
            </div>
            <div className="tokenomics-item">
              <h3 className="tokenomics-title">Token Type</h3>
              <p className="section-content">Utility token on the Base chain (deployed via Cliza Systems).</p>
            </div>
            <div className="tokenomics-item">
              <h3 className="tokenomics-title">Total Supply</h3>
              <p className="section-content">1,000,000,000 LYSIA</p>
            </div>
            <div className="tokenomics-item">
              <h3 className="tokenomics-title">Fee Redistribution</h3>
              <p className="section-content">A portion of transaction fees supports basic operational costs, ensuring the project’s sustainability.</p>
            </div>
            <p className="section-content emphasis">LYSIA’s tokenomics are designed to support a sustainable, community-driven ecosystem on the Base chain.</p>
          </div>
        </div>
      </section>

      {/* Deployment Section */}
      <section className="section deployment-section">
        <div className="container">
          <div className="section-content-wrapper">
            <div className="section-text">
              <h2 className="section-title">Deployment via Cliza Systems</h2>
              <p className="section-content">
                LYSIA is launching on the Base chain through Cliza Systems, a protocol that ensures a fair and community-driven token distribution. Cliza Systems’ infrastructure provides a stable foundation for LYSIA’s ecosystem, enabling seamless user engagement and organic growth within the Base chain community.
              </p>
            </div>
            <img src="/image5.jpg" alt="LYSIA Deployment Graphic" className="section-media" />
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="section roadmap-section">
        <div className="container">
          <h2 className="section-title">Roadmap</h2>
          <div className="roadmap-timeline">
            <div className="roadmap-item">
              <h3 className="roadmap-title">Q2 2025</h3>
              <p className="section-content">Official launch on Base chain via Cliza Systems, token distribution, and rollout of navigation support and community connection features.</p>
            </div>
            <div className="roadmap-item">
              <h3 className="roadmap-title">Q3 2025</h3>
              <p className="section-content">Launch of the transaction tracking dashboard to enhance user experience and transparency.</p>
            </div>
            <div className="roadmap-item">
              <h3 className="roadmap-title">Q4 2025 (Post-Vesting)</h3>
              <p className="section-content">Introduce the social tipping system to foster deeper community collaboration, alongside improvements to the navigation hub.</p>
            </div>
            <div className="roadmap-item">
              <h3 className="roadmap-title">2026</h3>
              <p className="section-content">Explore partnerships to expand LYSIA’s capabilities, potentially integrating with other Base chain projects or introducing new features to enhance user engagement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why LYSIA Section */}
      <section className="section why-section">
        <div className="container">
          <div className="section-content-wrapper">
            <div className="section-text">
              <h2 className="section-title">Why LYSIA Matters</h2>
              <p className="section-content">
                LYSIA is more than an AI agent; she’s a catalyst for connection and empowerment on the Base chain. In a 2025 crypto landscape where accessibility and collaboration are paramount, LYSIA stands out by offering a simple, intuitive way for users to engage with decentralized finance. Her focus on navigation, community building, and transaction tracking creates a welcoming environment where every user can thrive.
                <br /><br />
                As the Base chain ecosystem continues to grow, LYSIA will evolve alongside it, becoming an indispensable guide for those navigating the decentralized future. With her community-first approach, LYSIA proves that the true power of blockchain lies in the connections we forge together.
              </p>
            </div>
            <img src="/image6.jpg" alt="LYSIA Why Graphic" className="section-media" />
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="section faqs-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="section-content">
            <div className={`faq-item ${activeFaq === 0 ? 'active' : ''}`} onClick={() => toggleFaq(0)}>
              <h3 className="faq-question">What is LYSIA?</h3>
              <p className="faq-answer">LYSIA (Layered Synthetic Intelligence Avatar) is an AI-powered project on the Base chain, designed to simplify blockchain navigation and foster community connections. She helps users explore DeFi and the Base chain ecosystem while providing tools for collaboration and transaction tracking.</p>
            </div>
            <div className={`faq-item ${activeFaq === 1 ? 'active' : ''}`} onClick={() => toggleFaq(1)}>
              <h3 className="faq-question">When does LYSIA launch?</h3>
              <p className="faq-answer">LYSIA officially launches on the Base chain in Q2 2025 via Cliza Systems, with token distribution and initial features rolling out then.</p>
            </div>
            <div className={`faq-item ${activeFaq === 2 ? 'active' : ''}`} onClick={() => toggleFaq(2)}>
              <h3 className="faq-question">What features does LYSIA offer?</h3>
              <p className="faq-answer">LYSIA offers a navigation hub, community connection platform, transaction tracking dashboard, and a post-vesting social tipping system, all designed to enhance the Base chain experience.</p>
            </div>
            <div className={`faq-item ${activeFaq === 3 ? 'active' : ''}`} onClick={() => toggleFaq(3)}>
              <h3 className="faq-question">How can I join the LYSIA community?</h3>
              <p className="faq-answer">Join us on Telegram (link coming soon) to connect with the LYSIA community, get updates on the launch, and participate in discussions. Follow us on X (@lysia_ai) for the latest news.</p>
            </div>
            <div className={`faq-item ${activeFaq === 4 ? 'active' : ''}`} onClick={() => toggleFaq(4)}>
              <h3 className="faq-question">What is the total supply of $LYSIA?</h3>
              <p className="faq-answer">The total supply of $LYSIA is 1,000,000,000 tokens, deployed on the Base chain via Cliza Systems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p className="copyright">© 2025 LYSIA. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;